


































































import { computed, defineComponent, PropType, watch, ref, toRefs } from '@vue/composition-api';
import VirtualList from 'vue-virtual-scroll-list';
import defaultAvatar from '@tt4b/ttam-assets/src/images/creative/default-avatar.png';
import DropdownPanel from '@/components/common/dropdown-panel/index.vue';
import SelectNoDataIcon from '@tt4b/ttam-assets/src/svgs/select-no-data.svg';
import ListItem from './list-item.vue';
import { IdentityFullInfo } from '../types';
import { Message } from 'byted-vui';
import { i18n } from './../../../i18n';
import { trackEvent } from './../../../utils/index';
import { IDENTITY_TYPE_MAP, IdentityInfo, AvatarIcon } from '@tt4b/ttam-enums';
import TtamIdentityListItemDeleteModal from './delete-modal.vue';
import { fetchDeleteIdentity } from '@tt4b/ttam-hooks';
import { useIdentityAvatarUrl } from '../hooks';

const name = 'ttam-identity-list';

export default defineComponent({
  name,
  components: {
    DropdownPanel,
    VirtualList,
    SelectNoDataIcon,
    TtamIdentityListItemDeleteModal,
  },
  props: {
    dataSource: {
      type: Array as PropType<IdentityFullInfo[]>,
      default: () => [],
    },
    value: {
      type: String,
      default: '',
    },
    curItem: {
      type: Object as PropType<IdentityInfo>,
      default: () => ({
        displayName: '',
        avatarIcon: {
          width: 20,
          height: 20,
          webUri: '',
          url: '',
        },
        identityId: '',
      }),
    },
    loading: {
      type: Boolean,
      default: false,
    },
    isEmpty: {
      type: Boolean,
      default: false,
    },
    searchable: {
      type: Boolean,
      default: false,
    },
    placeholder: {
      type: String,
      default: '',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    isSupportEditAndDelete: {
      type: Boolean,
      default: false,
    },
    noAccessIdentityInfo: {
      type: Object as PropType<IdentityInfo>,
      default: () => ({
        displayName: '',
        avatarIcon: {
          width: 20,
          height: 20,
          webUri: '',
          url: '',
        },
        identityId: '',
      }),
    },
    showNoAccessIdentityInfo: {
      type: Boolean,
      default: false,
    },
  },
  setup(props, { emit, slots }) {
    const {
      dataSource,
      value,
      searchable,
      curItem: defaultCurItem,
      showNoAccessIdentityInfo,
      noAccessIdentityInfo,
      disabled,
    } = toRefs(props);
    const popperVisible = ref(false);
    const keywords = ref('');
    const formatDefaultCurItem = computed(() => ({ ...defaultCurItem.value, id: defaultCurItem.value?.identityId }));
    // 编辑场景如果假账号被删除，需要回显 creative/detail 中的数据
    const curItem = computed(() => dataSource.value.find(i => i.id === value.value) || formatDefaultCurItem.value);
    // 存在后端数据返回没有 displayName 的情况
    const curName = computed(() =>
      showNoAccessIdentityInfo.value
        ? noAccessIdentityInfo.value.displayName
        : curItem.value?.displayName ?? curItem.value?.id,
    );
    const curAvatar = computed(() =>
      useIdentityAvatarUrl(
        showNoAccessIdentityInfo.value
          ? noAccessIdentityInfo.value.avatarIcon
          : (curItem.value?.avatarIcon as AvatarIcon),
      ),
    );
    const curId = computed(() => curItem.value?.id);
    const curDataSource = computed(() => {
      const search = (str, input) => str?.toLowerCase().includes(input?.trim().toLowerCase());
      return searchable.value
        ? dataSource.value.filter(i => search(i.id, keywords.value) || search(i.displayName, keywords.value))
        : dataSource.value;
    });
    const isSearchEmpty = computed(() => searchable.value && keywords.value && curDataSource.value.length === 0);

    watch(popperVisible, v => {
      if (!v) {
        keywords.value = '';
      }
    });

    const onClick = (val: IdentityFullInfo) => {
      emit('input', val);
      popperVisible.value = false;
    };

    // 向外提供方法触发展开下拉
    const onFocus = () => {
      popperVisible.value = true;
    };

    const onPopoverCTA = (val: IdentityFullInfo) => {
      emit('popover-click', val);
      val.popoverCtaTea && trackEvent(val.popoverCtaTea);
    };

    const onKeywordInput = (val: string) => {
      emit('keyword-input', val);
      trackEvent('search_custom_identity');
    };
    const deleteDialogVisible = ref(false);
    const deleteIdentityData = ref({} as any);
    const deleteConfirmLoading = ref(false);

    const handleEditIdentity = (item: IdentityFullInfo) => {
      emit('edit-identity', item);
    };

    const handleDeleteIdentity = (item: IdentityFullInfo) => {
      deleteDialogVisible.value = true;
      deleteIdentityData.value = item;
    };
    const onCloseDeleteDialog = () => {
      deleteDialogVisible.value = false;
      deleteIdentityData.value = {};
    };
    const onConfirmDeleteDialog = async () => {
      if (deleteIdentityData.value?.identityId) {
        deleteConfirmLoading.value = true;
        try {
          const res = await fetchDeleteIdentity({
            identityId: deleteIdentityData.value?.identityId || '',
            identityType: deleteIdentityData.value?.identityType || 1,
          });
          deleteConfirmLoading.value = false;
          if (!res) {
            return;
          }
          if (deleteIdentityData.value?.identityId === value.value) {
            // 如果当前 creative 层级选中的 identity 账号为我们即将删除的账号则清空 creative 层级回显的选中数据，以默认值填充
            emit('identity-change', {
              identityId: '',
              identityType: IDENTITY_TYPE_MAP.FAKE_USER,
            });
          }
          Message.success(i18n.t('module_creative_identity_delete_success_text') as string);
          emit('delete-success', deleteIdentityData.value);
          onCloseDeleteDialog();
          trackEvent('delete_custom_identity_submit', {
            result: 'true',
            message: '',
          });
        } catch (err) {
          deleteConfirmLoading.value = false;
          trackEvent('delete_custom_identity_submit', {
            result: 'false',
            message: err || '',
          });
        }
      }
    };
    const toggleVisible = () => {
      if (disabled.value) {
        return;
      }
      popperVisible.value = !popperVisible.value;
    };
    return {
      name,
      curId,
      curName,
      keywords,
      curAvatar,
      defaultAvatar,
      curDataSource,
      popperVisible,
      isSearchEmpty,
      deleteDialogVisible,
      deleteConfirmLoading,
      ListItem,
      onClick,
      onFocus,
      onPopoverCTA,
      onKeywordInput,
      handleEditIdentity,
      handleDeleteIdentity,
      onCloseDeleteDialog,
      onConfirmDeleteDialog,
      toggleVisible,
    };
  },
});
